class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.onSearchFocusRecovery = this.onSearchFocusRecovery.bind(this);

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');
    this.detailsContainer.addEventListener('toggle', () => {
      if (!this.isOpen()) document.removeEventListener('keydown', this.onSearchFocusRecovery, true);
    });
  }

  connectedCallback() {
    if (!this.matches('.header__search.large-up-hide')) return;
    // Match Dawn's desktop breakpoint and release a mobile dialog on resize.
    this.desktopSearchQuery ||= window.matchMedia('(min-width: 990px)');
    this.onSearchViewportChange ||= () => {
      if (this.desktopSearchQuery.matches && this.isOpen()) this.close(false);
    };
    this.desktopSearchQuery.addEventListener('change', this.onSearchViewportChange);
    this.onSearchViewportChange();
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  onSearchFocusRecovery(event) {
    if (
      event.key !== 'Tab' || event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey ||
      event.isComposing || !this.isConnected || !this.isOpen()
    ) return;

    const dialog = this.detailsContainer.querySelector('[role="dialog"]');
    if (!dialog || dialog.contains(document.activeElement)) return;
    const visible = (element) => {
      if (!element || !element.getClientRects().length) return false;
      for (let current = element; current instanceof Element; current = current.parentElement || current.getRootNode().host) {
        const style = getComputedStyle(current);
        if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) <= 0.01) return false;
      }
      return true;
    };
    if (!visible(dialog)) return;

    // Yield to a second modal, including the existing Forms overlay in its
    // shadow root. An inline contact form does not suspend the search dialog.
    const otherModal = [...document.querySelectorAll('[aria-modal="true"], dialog[open]')]
      .some((element) => !this.contains(element) && visible(element));
    const formsOverlay = [...document.querySelectorAll('shopify-forms-embed')]
      .some((host) => visible(host.shadowRoot?.querySelector(
        '#app-embed[data-layout="overlay"] section[data-sizing="form-wrapper"]'
      )));
    if (otherModal || formsOverlay) return;

    const content = this.detailsContainer.querySelector('[tabindex="-1"]');
    if (!content || !visible(content)) return;
    const controls = getFocusableElements(content).filter(visible);
    const input = content.querySelector('input:not([type="hidden"]):enabled');
    const target = event.shiftKey ? controls[controls.length - 1] : visible(input) ? input : controls[0];
    if (!target) return;
    event.preventDefault();
    // Re-establish Dawn's own trap after an external modal returns focus to
    // BODY; normal input within the search continues to use the same trap.
    trapFocus(content, target);
  }

  open(event) {
    if (this.desktopSearchQuery?.matches) return;
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
    if (this.classList.contains('header__search')) {
      document.addEventListener('keydown', this.onSearchFocusRecovery, true);
    }
  }

  close(focusToggle = true) {
    document.removeEventListener('keydown', this.onSearchFocusRecovery, true);
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }

  disconnectedCallback() {
    this.desktopSearchQuery?.removeEventListener('change', this.onSearchViewportChange);
    document.removeEventListener('keydown', this.onSearchFocusRecovery, true);
  }
}

customElements.define('details-modal', DetailsModal);
